import * as R from 'ramda'

import {
  AddIncidentPayload,
  DeleteIncident,
  isAddIncident,
  isUpdateIncident,
  UpdateIncidentPayload,
} from '@collector/desktop-feature-incidents-queue-core'
import {
  getOppositeParticipant,
  getParticipant,
} from '@collector/shared-ui-domain'
import { Incident, Participant } from '@collector/sportsapi-client'
import { OccursAction } from '@collector/sportsapi-types'

import {
  useEvent,
  useEventDetails,
  useEventSettings,
  useParticipants,
  useSubParticipants,
} from '../../composables'
import { injectEventIncidentsQueue } from '../../provide-inject'
import {
  injectIncidentsQueuePayloadConfig,
  injectIncidentsTeamPossessionConfig,
} from '../provide-inject'
import { useIncidentClock } from './useIncidentClock'

export type AddIncidentPayloadWithOptionalTime = Omit<
  AddIncidentPayload,
  'minute' | 'second'
> &
  Partial<Pick<AddIncidentPayload, 'minute' | 'second'>>

export type UpdateIncidentPayloadWithoutIncidentId = Omit<
  UpdateIncidentPayload,
  'incident_id'
>

type AddIncidentOptions = {
  shouldResetPossession?: boolean
}

type UseEventIncidentsQueue = {
  /**
   * @returns uuid of the added incident, or void if uuid is not needed in function caller
   */
  addIncident(
    payload: AddIncidentPayloadWithOptionalTime,
    opts?: AddIncidentOptions,
  ): string | void
  updateIncident(
    payload: UpdateIncidentPayloadWithoutIncidentId,
    incident: Pick<Incident, 'id' | 'incident_id'>,
  ): void
  deleteIncident(id: DeleteIncident['id']): void
  changeIncidentOrder(
    incident: Pick<Incident, 'id' | 'incident_id'>,
    action: OccursAction,
  ): void
}

export function useEventIncidentsQueue(): UseEventIncidentsQueue {
  const eventIncidentsQueue = injectEventIncidentsQueue()
  const incidentsQueuePayloadConfig = injectIncidentsQueuePayloadConfig()
  const incidentsTeamPossessionConfig = injectIncidentsTeamPossessionConfig()

  const { event } = useEvent()
  const { homeParticipant, awayParticipant } = useParticipants()
  const { createIncidentTime } = useIncidentClock()
  const { updateEventSettings, eventSettings } = useEventSettings()
  const { homeSubParticipants, awaySubParticipants } = useSubParticipants()
  const { getEventDetailValue } = useEventDetails()

  /**
   * Checks if there's already a duplicated incident payload in the queue and logs it to Sentry.
   */
  function isDuplicatedIncidentPayload(
    payload: AddIncidentPayload | UpdateIncidentPayload,
  ): boolean {
    const queueIncidentEqualPayload = eventIncidentsQueue.queue.find(
      (queueIncident) =>
        (isAddIncident(queueIncident) || isUpdateIncident(queueIncident)) &&
        R.equals(queueIncident.payload, payload) &&
        queueIncident.status !== 'Error',
    )

    if (queueIncidentEqualPayload) {
      console.error('duplicated incident payload', payload) // eslint-disable-line

      return true
    }

    return false
  }

  function getParticipants(participantTeamId: number | undefined | null): {
    participant: Participant
    oppositeParticipant: Participant
  } {
    const home = homeParticipant.value
    const away = awayParticipant.value
    if (!home || !away) {
      throw new Error('Participants not loaded')
    }

    return {
      participant: getParticipant(participantTeamId, home, away),
      oppositeParticipant: getOppositeParticipant(
        participantTeamId,
        home,
        away,
      ),
    }
  }

  /**
   * Add incident payload on queue to create incident, with:
   * - current event time
   * - current event status
   * - optionally using incidentsQueuePayloadConfig for specific incidentId
   * - optionally using pitchIncidentsConfig or resetPossessionIncidents to reset possession
   */
  function addIncident(
    payload: AddIncidentPayloadWithOptionalTime,
    opts?: AddIncidentOptions,
  ): string | void {
    // eslint-disable-next-line no-console
    console.debug(
      'useEventIncidentsQueue.addIncident',
      JSON.stringify(payload),
      +new Date(),
    )

    const enhancedPayload: AddIncidentPayload = {
      status_id: event.value.status_id,
      ...createIncidentTime(payload.incident_id),
      ...payload,
    }

    if (isDuplicatedIncidentPayload(enhancedPayload)) {
      return
    }

    const addIncident =
      incidentsQueuePayloadConfig?.[payload.incident_id]?.addIncident

    let uuid: string | void

    if (addIncident) {
      const home = homeParticipant.value
      const away = awayParticipant.value
      if (!home || !away) return

      const participants = payload.participant_team_id
        ? getParticipants(payload.participant_team_id)
        : { participant: null, oppositeParticipant: null }

      uuid = addIncident(enhancedPayload, eventIncidentsQueue, {
        ...participants,
        updateEventSettings,
        homeParticipant: home,
        awayParticipant: away,
        homeSubParticipants: homeSubParticipants.value,
        awaySubParticipants: awaySubParticipants.value,
        getEventDetailValue,
        eventSettings: eventSettings.value,
      })
    } else {
      uuid = eventIncidentsQueue.addIncident(enhancedPayload)
    }
    if (
      opts &&
      opts.shouldResetPossession &&
      incidentsTeamPossessionConfig?.resetPossessionIncidents?.has(
        payload.incident_id,
      )
    ) {
      updateEventSettings({ teamSidePossession: undefined })
    }

    return uuid
  }

  /**
   * Add incident payload on queue to update incident, with:
   * - current event status
   * - optionally using incidentsQueuePayloadConfig for specific incidentId
   */
  function updateIncident(
    payload: Omit<UpdateIncidentPayload, 'incident_id'>,
    incident: Pick<
      Incident,
      'id' | 'incident_id' | 'participant_team_id' | 'minute' | 'second'
    >,
  ): void {
    const enhancedPayload: UpdateIncidentPayload = {
      incident_id: incident.incident_id,
      status_id: event.value.status_id,
      // even if `minute` and `second` are not being updated, they must be redundantly added to payload,
      // because API can't properly calculate statistics without them.
      minute: incident.minute,
      second: incident.second,
      ...payload,
    }

    if (isDuplicatedIncidentPayload(enhancedPayload)) {
      return
    }

    const updateIncident =
      incidentsQueuePayloadConfig?.[incident.incident_id]?.updateIncident

    if (updateIncident) {
      updateIncident(
        enhancedPayload,
        incident.id,
        eventIncidentsQueue,
        getParticipants(incident.participant_team_id),
      )
    } else {
      eventIncidentsQueue.updateIncident(enhancedPayload, incident.id)
    }
  }

  function changeIncidentOrder(
    incident: Pick<Incident, 'id' | 'incident_id'>,
    action: OccursAction,
  ): void {
    eventIncidentsQueue.changeIncidentOrder(incident.id, action)
  }

  return {
    addIncident,
    updateIncident,
    changeIncidentOrder,
    deleteIncident:
      eventIncidentsQueue.deleteIncident.bind(eventIncidentsQueue),
  }
}
